/**
 * Scroll reveal for the home page.
 *
 * Each section rises and fades in as it scrolls into view, then its parts
 * follow in turn: heading label, title and note, collection rows, footer
 * columns. Sections that animate themselves (spotlight, feature panel) are
 * left to their own entrance. The product stage replays its active slide.
 *
 * Content stays visible without JavaScript and for people who prefer reduced
 * motion: the hidden starting state only applies once `html.js-reveal` is set.
 */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!reducedMotion && 'IntersectionObserver' in window) {
  const root = document.documentElement;
  root.classList.add('js-reveal');

  const sections = [
    ...document.querySelectorAll('#MainContent > .shopify-section'),
    ...document.querySelectorAll('footer > .shopify-section'),
  ];

  // Parts that follow the section in, in reading order.
  const partSelectors = [
    '.pdp-head .pdp-label',
    '.pdp-head .pdp-title',
    '.pdp-head .pdp-head__aside',
    '.home-rows > li',
    '.home-stage__controls',
    '.home-stage__rail',
    '.site-foot__signup',
    '.site-foot__col',
    '.site-foot__bar',
  ].join(',');

  const reveal = (section) => {
    section.classList.add('is-revealed');
    const stage = section.querySelector('home-stage');
    if (stage) {
      // Replay the active slide's entrance now that it is on screen.
      const active = stage.querySelector('.home-stage__slide[data-active]');
      if (active) {
        active.removeAttribute('data-active');
        requestAnimationFrame(() => requestAnimationFrame(() => active.setAttribute('data-active', '')));
      }
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target);
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
  );

  sections.forEach((section, index) => {
    // The first section is the hero: it holds the page's largest image, so it
    // is never hidden. Hiding it until this script runs delays the LCP.
    if (index === 0) return;
    const ownEntrance = section.querySelector('home-spotlight, home-reveal');
    section.classList.add(ownEntrance ? 'reveal-parts' : 'reveal');

    section.querySelectorAll(partSelectors).forEach((part, order) => {
      part.classList.add('reveal-part');
      part.style.setProperty('--reveal-order', String(Math.min(order, 8)));
    });

    observer.observe(section);
  });

  // Fast flicks can carry a section past the viewport between observer
  // checks, so anything already at or above the fold is revealed too.
  let queued = false;
  const catchUp = () => {
    queued = false;
    const fold = window.innerHeight * 0.88;
    for (const section of sections) {
      if (section.classList.contains('is-revealed')) continue;
      if (section.getBoundingClientRect().top < fold) {
        reveal(section);
        observer.unobserve(section);
      }
    }
  };
  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(catchUp);
  };
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onScroll, { passive: true });
}
